<template>
  <div class="public-views-page">
  <ViewMuseum v-if="museumMode && !adminMode && $route.path === '/views'" :auto-tour="$route.query.showcase === 'tour'" :showcase-exhibition="$route.query.showcase === 'exhibition'" :showcase-view-id="String($route.query.showcaseView || '')" :showcase-cycle="Number($route.query.showcaseCycle || 0)" :showcase-frame="$route.query.galleryFrame === '1'" :showcase-loop="$route.query.showcaseLoop === '1'" :showcase-controls="$route.query.showcaseControls === '1'" :showcase-channel="String($route.query.showcaseChannel || '')" :views="visibleViews" :collection="views" :reading="reading?.text || ''" :query="query" :loading="loading" :searching="searching" :error="error" @query="query=$event" @retry="refreshViews()" @website="showArticleList" />
  <main v-show="!museumMode || adminMode" class="public-views">
    <header class="public-views-hero" data-scroll-group data-scroll-step="28">
      <div class="public-views-hero-heading">
        <h1>See Henry through <span>your own lens</span></h1>
        <button class="public-views-project" type="button" aria-haspopup="dialog" :aria-expanded="noteOpen" @click="noteOpen=true">
          <span>A note from Henry</span>
          <ArrowUpRightIcon />
        </button>
      </div>
      <div class="public-views-hero-copy">
      <p class="public-views-dek">Start with what you&rsquo;d like to explore about Henry. The site&rsquo;s AI will create a <AiViewTerm class="view-term-definition" /> around your question or perspective. You can follow the links to explore the original work in more detail, and share your <AiViewTerm /> here if you&rsquo;d like others to read it too.</p>
        <button class="public-views-invitation" type="button" @click="createOwnView">
          <svg class="public-views-compose-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 4H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h13a2 2 0 0 0 2-2v-7" />
            <path class="public-views-compose-pen" d="m15.5 4.5 4 4M10 14l4-1 7-7a2.83 2.83 0 0 0-4-4l-7 7-1 5Z" />
          </svg>
          <span class="public-views-invitation-copy">Compose your own <AiViewTerm /></span>
        </button>
      </div>
    </header>

    <YourViews v-if="!adminMode"/>
    <section id="public-views-collection" class="public-views-collection" aria-labelledby="public-views-collection-title">
      <div class="public-views-collection-heading" data-scroll-reveal>
        <div>
          <div class="public-views-title-line">
            <h2 id="public-views-collection-title"><AiViewTerm plural /></h2>
            <button
              class="public-views-admin-trigger"
              type="button"
              aria-label="Open collection management"
              :aria-expanded="adminLoginOpen || adminMode"
              @click="openAdminLogin"
            >
              <span aria-hidden="true">✦</span>
            </button>
          </div>
          <p v-if="!reading || !reading.text">See how other visitors have looked across Henry&rsquo;s work, experiences, and ideas.</p>
          <p v-else>{{ reading.text }}</p>
        </div>
      </div>
      <Transition name="public-admin-panel">
        <form v-if="adminLoginOpen && !adminMode" class="public-views-admin-login" @submit.prevent="loginAdmin">
          <div>
            <strong>Curate Shared Views</strong>
            <p>Enter the collection password to manage what appears here.</p>
          </div>
          <label>
            <span class="sr-only">Collection password</span>
            <input
              ref="adminPassword"
              v-model="adminPassword"
              type="password"
              autocomplete="current-password"
              placeholder="Password"
              :disabled="adminBusy"
            />
          </label>
          <button type="submit" :disabled="adminBusy || !adminPassword">{{ adminBusy ? 'Checking…' : 'Continue' }}</button>
          <button class="public-views-admin-close" type="button" aria-label="Close" @click="closeAdminLogin">×</button>
          <p v-if="adminError" class="public-views-admin-error" role="alert">{{ adminError }}</p>
        </form>
      </Transition>
      <div v-if="adminMode" class="public-views-admin-bar">
        <div>
          <strong>Curating collection</strong>
          <span>{{ publicAdminCount }} public · {{ hiddenAdminCount }} hidden</span>
        </div>
        <button type="button" :disabled="loading" @click="refreshAdminViews">Refresh</button>
        <button type="button" @click="signOutAdmin">Sign out</button>
      </div>
      <div v-if="adminMode" class="public-views-reading-admin">
        <div>
          <strong>Recent curiosity</strong>
          <p>Refresh the short note with recent public questions.</p>
          <p v-if="readingMessage" class="public-views-reading-message" role="status">{{ readingMessage }}</p>
        </div>
        <button type="button" :disabled="readingBusy || !publicAdminCount" @click="updateReading">
          <span v-if="readingBusy" class="reading-update-dot" aria-hidden="true"></span>
          {{ readingBusy ? 'Updating note…' : 'Update note' }}
        </button>
      </div>
      <div class="timeline-tools public-views-tools" data-scroll-reveal>
        <label class="timeline-search public-views-search" :aria-label="adminMode ? 'Search managed Views' : 'Search public Views'">
          <input v-model="query" class="timeline-search-input" type="search" autocomplete="off" spellcheck="false" :placeholder="adminMode ? 'Search all managed Views' : 'Search the collection'" />
          <i class="fa fa-search timeline-search-icon" aria-hidden="true"></i>
        </label>
      </div>

      <div v-if="loading" class="public-views-status">Loading <AiViewTerm plural />…</div>
      <div v-else-if="error" class="public-views-status">
        <p role="status">{{ error }}</p>
        <button class="public-views-retry" type="button" @click="refreshViews()">Try again</button>
      </div>
      <div v-else-if="searching" class="public-views-status">Looking through public <AiViewTerm plural />…</div>
      <TransitionGroup
        v-else-if="visibleViews.length"
        appear
        tag="ol"
        name="public-view-reveal"
        class="public-views-list"
        :class="{ 'is-curating': adminMode }"
      >
        <li
          v-for="(view, index) in visibleViews"
          :key="view.id"
          :class="{ 'is-admin-hidden': adminMode && view.visibility === 'hidden', 'is-admin-pending': pendingViewId === view.id }"
          :style="{ '--public-view-delay': `${Math.min(index, 6) * 28}ms` }"
        >
          <router-link
            :to="`/view/${view.id}`"
            class="public-view-link"
            :class="{ 'has-illustration': view.illustration && view.illustration.src }"
          >
            <figure v-if="view.illustration && view.illustration.src" class="public-view-plate">
              <img
                :src="view.illustration.src"
                :alt="view.illustration.alt || ''"
                loading="lazy"
                decoding="async"
              />
            </figure>
            <div class="public-view-body">
              <span class="public-view-question">&ldquo;{{ view.question }}&rdquo;</span>
              <strong><ViewReadingTitle :text="view.title" /></strong>
              <span class="public-view-meta">{{ sourceLabel(view.source_count) }} · {{ dateLabel(view.created_at) }} <ArrowUpRightIcon /></span>
            </div>
          </router-link>
          <div
            v-if="adminMode"
            class="public-view-admin-controls"
            role="group"
            :aria-label="`Manage ${view.title}`"
          >
            <span class="public-view-visibility" :class="`is-${view.visibility}`">{{ view.visibility === 'public' ? 'Public' : 'Hidden' }}</span>
            <button type="button" :disabled="view.visibility !== 'public' || publicPosition(view.id) === 0 || Boolean(pendingViewId)" @click="pinView(view)">Top</button>
            <button type="button" :disabled="view.visibility !== 'public' || publicPosition(view.id) === 0 || Boolean(pendingViewId)" @click="moveView(view, -1)">Earlier</button>
            <button type="button" :disabled="view.visibility !== 'public' || publicPosition(view.id) === publicAdminCount - 1 || Boolean(pendingViewId)" @click="moveView(view, 1)">Later</button>
            <button type="button" :disabled="Boolean(pendingViewId)" @click="toggleViewVisibility(view)">{{ view.visibility === 'public' ? 'Hide' : 'Publish' }}</button>
            <button class="is-destructive" type="button" :disabled="Boolean(pendingViewId)" @click="deleteView(view)">Delete</button>
          </div>
        </li>
      </TransitionGroup>
      <p v-else-if="query.trim()" class="public-views-status">No {{ adminMode ? 'managed' : 'public' }} <AiViewTerm /> matches this search.</p>
      <p v-else class="public-views-status">No one has shared a <AiViewTerm /> yet.</p>
    </section>

    <section v-if="adminMode" class="view-records" aria-labelledby="view-records-heading">
      <header class="view-records-heading">
        <h2 id="view-records-heading">All saved <AiViewTerm plural /></h2>
        <p>Open any saved View. Private Views appear last and stay out of the public collection.</p>
      </header>
      <ol class="view-records-list">
        <li v-for="record in records" :key="record.id">
          <router-link :to="`/view/${record.id}`" class="view-record-link">
            <div class="view-record-copy">
              <strong><ViewReadingTitle :text="record.title" /></strong>
              <span>{{ record.question }}</span>
            </div>
            <span class="view-record-state">{{ record.visibility === 'private' ? 'Private' : record.visibility === 'public' ? 'Public' : 'Hidden' }}</span>
            <time :datetime="new Date(record.created_at).toISOString()">{{ dateLabel(record.created_at) }}</time>
            <ArrowUpRightIcon />
          </router-link>
        </li>
      </ol>
      <p v-if="recordsError" role="alert" class="public-views-status">{{ recordsError }}</p>
      <button v-if="recordsError || recordsNextOffset !== null" class="view-records-more" :disabled="recordsBusy" @click="refreshRecords(recordsNextOffset !== null)">
        {{ recordsBusy ? 'Loading…' : recordsError ? 'Try again' : 'Show more' }}
      </button>
      <p v-else-if="recordsBusy" class="public-views-status">Loading saved Views…</p>
      <p v-else-if="!records.length" class="public-views-status">No saved Views yet.</p>
    </section>
    <PrivateVisitorActivity v-if="adminMode" />
    <section v-if="adminMode" data-activity-private class="view-records visit-feedback-records" aria-labelledby="visit-feedback-heading">
      <header class="view-records-heading"><h2 id="visit-feedback-heading">Exhibition feedback</h2><p>Private ratings and notes from visitors.</p></header>
      <ol class="view-records-list"><li v-for="entry in feedback" :key="entry.id" class="visit-feedback-entry">
        <div><router-link :to="`/view/${entry.share_id}`"><ViewReadingTitle :text="entry.title"/></router-link><span>{{entry.rating}} / 5</span></div>
        <p v-if="entry.text">{{entry.text}}</p><time :datetime="new Date(entry.created_at).toISOString()">{{dateLabel(entry.created_at)}}</time><p class="feedback-visitor-details">{{visitorContextLabel(entry.visitor_context_json)}}</p>
      </li></ol>
      <p v-if="feedbackError" role="alert">{{feedbackError}}</p>
      <p v-else-if="!feedback.length && !feedbackBusy">No feedback yet.</p>
      <button class="view-records-more" :disabled="feedbackBusy" @click="refreshFeedback(feedbackOffset !== null)">{{feedbackBusy?'Loading…':feedbackError?'Try again':feedbackOffset !== null?'Show more':'Refresh feedback'}}</button>
    </section>
  </main>
  <ExhibitionInvitation v-if="!museumMode && !adminMode && $route.path === '/views'" collection :collection-views="views" :view="{title:'Views',illustration:views[0]?.illustration}" @enter="openMuseum"/>
  <PublicViewsInfoDialog v-if="noteOpen" note @close="noteOpen = false" />
  </div>
</template>

<script>
import { visitorContextLabel } from '../utils/visitorContextLabel';
import YourViews from './view/YourViews.vue';
import ViewReadingTitle from './view/ViewReadingTitle.vue';
import ExhibitionInvitation from './view/ExhibitionInvitation.vue';
import {
  clearSharedViewsAdminSession,
  hasSharedViewsAdminSession,
  getCollectionSynthesis,
  listManagedViews,
  listViewRecords,
  listExhibitionFeedback,
  listPublicViews,
  loginSharedViewsAdmin,
  reorderManagedViews,
  updateManagedView,
  updateCollectionSynthesis,
} from '../utils/viewShareSite';
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import AiViewTerm from './ui/AiViewTerm.vue';
import { revealViewTransition } from '../utils/viewPresentationTransition';
import { defineAsyncComponent } from 'vue';
import { prefersMuseum, consumeMuseumResume, enterMuseumFromGallery, museumSession, updateMuseumSession } from '../utils/viewMuseumSession';
const PrivateVisitorActivity = defineAsyncComponent(() => import(/* webpackChunkName: 'private-activity' */ './view/PrivateVisitorActivity.vue'));
const ViewMuseum = defineAsyncComponent(() => import(/* webpackChunkName: 'view-museum' */ './view/ViewMuseum.vue'));
import PublicViewsInfoDialog from './view/PublicViewsInfoDialog.vue';

export default {
  name: 'PublicViewsPage',
  components: { PrivateVisitorActivity, YourViews, ViewReadingTitle, ExhibitionInvitation, ViewMuseum, AiViewTerm, PublicViewsInfoDialog },
  mixins: [scrollRevealMixin],
  data() {
    const routeQuery = this.$route?.query || {};
    return {
      museumMode: ['tour', 'exhibition'].includes(routeQuery.showcase) || routeQuery.presentation === 'museum' || prefersMuseum(),
      views: [],
      noteOpen: false,
      adminViews: [],
      feedback: [], feedbackOffset: null, feedbackBusy: false, feedbackError: '',
      records: [],
      recordsNextOffset: null,
      recordsBusy: false,
      recordsError: '',
      searchResults: [],
      query: '',
      loading: true,
      searching: false,
      error: '',
      adminMode: false,
      adminLoginOpen: false,
      adminPassword: '',
      adminBusy: false,
      adminError: '',
      pendingViewId: '',
      reading: null,
      readingBusy: false,
      readingMessage: '',
    };
  },
  computed: {
    visibleViews() {
      const query = this.query.toLocaleLowerCase().trim();
      if (this.adminMode) {
        if (!query) return this.adminViews;
        return this.adminViews.filter((view) => `${view.question} ${view.title}`.toLocaleLowerCase().includes(query));
      }
      if (!query) return this.views;
      return this.searchResults;
    },
    publicAdminCount() {
      return this.adminViews.filter((view) => view.visibility === 'public').length;
    },
    hiddenAdminCount() {
      return this.adminViews.length - this.publicAdminCount;
    },
  },
  watch: {
    '$route.query.presentation'(value) {
      if (this.$route.path === '/views' && value === 'museum') this.forceGalleryEntrance();
    },
    museumMode(value) { if (!value) this.$nextTick(() => { this.refreshScrollReveal(); revealViewTransition(); }); },
    query() {
      window.clearTimeout(this._searchTimer);
      if (this.adminMode) {
        this.searchResults = [];
        this.searching = false;
        return;
      }
      const sequence = (this._searchSequence || 0) + 1;
      this._searchSequence = sequence;
      if (!this.query.trim()) {
        this.searchResults = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this._searchTimer = window.setTimeout(async () => {
        try {
          const data = await listPublicViews(this.query.trim());
          if (sequence === this._searchSequence) this.searchResults = data.views || [];
        } catch {
          if (sequence === this._searchSequence) {
            const query = this.query.toLocaleLowerCase().trim();
            this.searchResults = this.views.filter((view) => `${view.question} ${view.title}`.toLocaleLowerCase().includes(query));
          }
        } finally {
          if (sequence === this._searchSequence) this.searching = false;
        }
      }, 220);
    },
  },
  async mounted() {
    const galleryEntrance=this.$route.query.presentation==='museum';
    if(galleryEntrance) this.forceGalleryEntrance();
    const resume=galleryEntrance || this.$route.query.galleryFrame==='1'?null:consumeMuseumResume();
    if(resume){this.$router.replace(`/view/${resume.id}`);return;}
    this.query=museumSession().hall?.query || '';
    await Promise.all([this.refreshViews({ preferCache: true }), this.refreshReading()]);
    if (hasSharedViewsAdminSession() && this.$route.query.presentation !== 'museum') await this.resumeAdminSession();
    window.addEventListener('pageshow', this.refreshViews);
    window.addEventListener('keydown', this.handleAdminKeydown);
  },
  activated() {
    this.museumMode=['tour', 'exhibition'].includes(this.$route.query.showcase) || this.$route.query.presentation === 'museum' || prefersMuseum();
    const galleryEntrance=this.$route.query.presentation==='museum';
    if(galleryEntrance) this.forceGalleryEntrance();
    const resume=galleryEntrance || this.$route.query.galleryFrame==='1'?null:consumeMuseumResume();
    if(resume){this.$router.replace(`/view/${resume.id}`);return;}
    this.refreshViews();
  },
  beforeUnmount() {
    this._readingDisposed = true;
    window.clearTimeout(this._readingTimer);
    window.clearTimeout(this._searchTimer);
    window.removeEventListener('pageshow', this.refreshViews);
    window.removeEventListener('keydown', this.handleAdminKeydown);
  },
  methods: {
    visitorContextLabel,
    forceGalleryEntrance() { enterMuseumFromGallery(); this.adminMode = false; this.museumMode = true; },
    showArticleList() {
      this.museumMode = false;
      if (this.$route.query.presentation === 'museum') {
        const query = { ...this.$route.query };
        delete query.presentation;
        this.$router.replace({ path: '/views', query, hash: this.$route.hash });
      }
    },
    openMuseum() { updateMuseumSession({mode:'museum',resumeOnViews:false});this.museumMode=true; },
    openMuseumAdmin() {this.museumMode=false;this.$nextTick(this.openAdminLogin);},
    async refreshReading() {
      window.clearTimeout(this._readingTimer);
      try {
        const data = await getCollectionSynthesis(this.adminMode);
        if (this._readingDisposed) return;
        this.reading = data.synthesis;
        this.readingBusy = data.status === 'preparing';
        if (this.readingBusy) {
          this.readingMessage = 'Looking through recent questions…';
          this._readingTimer = window.setTimeout(() => this.refreshReading(), 3000);
        } else if (data.status === 'failed') {
          this.readingMessage = 'The note could not be updated. Please try again.';
        } else if (data.status === 'ready') {
          this.readingMessage = data.synthesis ? 'The note is published.' : 'Update the note to reflect changes to the collection.';
        }
      } catch (error) {
        if (this._readingDisposed) return;
        this.readingBusy = false;
        if (this.adminMode) this.readingMessage = error?.message === 'forbidden'
          ? 'Sign in again to update the note.'
          : 'The note is temporarily unavailable. Try updating it again.';
      }
    },
    async updateReading() {
      if (this.readingBusy) return;
      this.readingBusy = true;
      this.readingMessage = 'Looking through recent questions…';
      try {
        await updateCollectionSynthesis(crypto.randomUUID());
        await this.refreshReading();
      } catch (error) {
        if (error?.message === 'synthesis_busy') return this.refreshReading();
        this.readingBusy = false;
        this.readingMessage = error?.message === 'forbidden'
          ? 'Sign in again to update the note.'
          : 'The note could not be updated. Please try again.';
      }
    },
    async refreshViews({ preferCache = false } = {}) {
      if (this.adminMode) return this.refreshAdminViews();
      const sequence = (this._refreshSequence || 0) + 1;
      this._refreshSequence = sequence;
      this.loading = true;
      this.error = '';
      try {
        const query = this.query.trim();
        const [collection, search] = await Promise.all([
          listPublicViews('', { preferCache }),
          query ? listPublicViews(query) : Promise.resolve(null),
        ]);
        if (sequence !== this._refreshSequence) return;
        this.views = collection.views || [];
        if (search) this.searchResults = search.views || [];
      } catch {
        if (sequence === this._refreshSequence) this.error = 'Views are temporarily unavailable.';
      } finally {
        if (sequence === this._refreshSequence) this.loading = false;
      }
    },
    openAdminLogin() {
      if (this.adminMode) return;
      this.adminLoginOpen = true;
      this.adminError = '';
      this.$nextTick(() => this.$refs.adminPassword?.focus());
    },
    closeAdminLogin() {
      if (this.adminBusy) return;
      this.adminLoginOpen = false;
      this.adminPassword = '';
      this.adminError = '';
    },
    handleAdminKeydown(event) {
      if (event.key === 'Escape' && this.adminLoginOpen && !this.adminBusy) this.closeAdminLogin();
    },
    async loginAdmin() {
      if (!this.adminPassword || this.adminBusy) return;
      this.adminBusy = true;
      this.adminError = '';
      try {
        await loginSharedViewsAdmin(this.adminPassword);
        this.adminMode = true;
        this.adminLoginOpen = false;
        this.adminPassword = '';
        await this.refreshAdminViews();
      } catch (error) {
        this.adminError = error?.message === 'too_many_attempts'
          ? 'Too many attempts. Wait a moment and try again.'
          : 'That password did not work.';
      } finally {
        this.adminBusy = false;
      }
    },
    async resumeAdminSession() {
      try {
        this.adminMode = true;
        await this.refreshAdminViews();
      } catch {
        await this.signOutAdmin();
      }
    },
    async refreshAdminViews() {
      this.loading = true;
      this.error = '';
      try {
        const data = await listManagedViews();
        this.adminViews = data.views || [];
        await this.refreshRecords();
        await this.refreshFeedback();
        await this.refreshReading();
      } catch (error) {
        if (error?.message === 'forbidden') {
          clearSharedViewsAdminSession();
          this.adminMode = false;
          throw error;
        }
        this.error = 'Collection management is temporarily unavailable.';
      } finally {
        this.loading = false;
      }
    },
    async signOutAdmin() {
      clearSharedViewsAdminSession();
      this.adminMode = false;
      this.adminViews = [];
      this.records = [];
      this.feedback = []; this.feedbackOffset = null; this.feedbackError = '';
      this.recordsNextOffset = null;
      this.recordsError = '';
      this.pendingViewId = '';
      this.readingMessage = '';
      this.readingBusy = false;
      window.clearTimeout(this._readingTimer);
      await this.refreshViews();
    },
    async refreshFeedback(append = false) {
      if(this.feedbackBusy)return;
      this.feedbackBusy=true;this.feedbackError='';
      try {
        const data=await listExhibitionFeedback(append ? this.feedbackOffset : 0);
        if(this.adminMode){this.feedback=append?[...this.feedback,...data.feedback]:data.feedback;this.feedbackOffset=data.next_offset;}
      }catch{if(this.adminMode)this.feedbackError='Feedback could not be loaded. Please try again.';}
      finally{this.feedbackBusy=false;}
    },
    async refreshRecords(append = false) {
      if (this.recordsBusy) return;
      this.recordsBusy = true;
      this.recordsError = '';
      try {
        const data = await listViewRecords(append ? this.recordsNextOffset : 0);
        if (!this.adminMode) return;
        this.records = append ? [...this.records, ...data.records] : data.records;
        this.recordsNextOffset = data.next_offset;
      } catch {
        if (this.adminMode) this.recordsError = 'Saved Views could not be loaded. Please try again.';
      } finally {
        this.recordsBusy = false;
      }
    },
    publicPosition(id) {
      return this.adminViews.filter((view) => view.visibility === 'public').findIndex((view) => view.id === id);
    },
    async persistPublicOrder(ordered) {
      const hidden = this.adminViews.filter((view) => view.visibility !== 'public');
      const previous = this.adminViews;
      this.adminViews = [...ordered, ...hidden];
      this.pendingViewId = 'order';
      try {
        await reorderManagedViews(ordered.map((view) => view.id));
      } catch {
        this.adminViews = previous;
        this.error = 'The new order could not be saved. Please try again.';
      } finally {
        this.pendingViewId = '';
      }
    },
    moveView(view, direction) {
      const ordered = this.adminViews.filter((item) => item.visibility === 'public');
      const index = ordered.findIndex((item) => item.id === view.id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= ordered.length) return;
      [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
      this.persistPublicOrder(ordered);
    },
    pinView(view) {
      const ordered = this.adminViews.filter((item) => item.visibility === 'public' && item.id !== view.id);
      this.persistPublicOrder([view, ...ordered]);
    },
    async toggleViewVisibility(view) {
      this.pendingViewId = view.id;
      this.error = '';
      try {
        await updateManagedView(view.id, view.visibility === 'public' ? 'hide' : 'publish');
        await this.refreshAdminViews();
      } catch {
        this.error = 'That change could not be saved. Please try again.';
      } finally {
        this.pendingViewId = '';
      }
    },
    async deleteView(view) {
      if (!window.confirm(`Delete “${view.title}” permanently? This also removes its shared link.`)) return;
      this.pendingViewId = view.id;
      this.error = '';
      try {
        await updateManagedView(view.id, 'delete');
        this.adminViews = this.adminViews.filter((item) => item.id !== view.id);
        await this.refreshRecords();
        await this.refreshReading();
      } catch {
        this.error = 'This View could not be deleted. Please try again.';
      } finally {
        this.pendingViewId = '';
      }
    },
    sourceLabel(count) {
      return `${count} original ${count === 1 ? 'piece' : 'pieces'}`;
    },
    dateLabel(timestamp) {
      if (!timestamp) return 'Date unavailable';
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        .format(new Date(timestamp));
    },
    createOwnView() {
      window.dispatchEvent(new CustomEvent('open-site-view'));
    },
  },
};
</script>

<style scoped>
.visit-feedback-entry{padding:20px 0;}
.visit-feedback-entry>div{display:flex;justify-content:space-between;gap:20px;}
.visit-feedback-entry p{white-space:pre-wrap;overflow-wrap:anywhere;}
.visit-feedback-entry time{font-size:12px;color:var(--site-text-secondary);}
.view-records { margin-top: 72px; }
.view-records-heading { margin-bottom: 24px; }
.view-records-heading h2 { margin: 0 0 12px; font-size: 26px; }
.view-records-heading p { margin: 0; color: var(--site-text-secondary); font: 16px/1.5 'Quattrocento Sans', sans-serif; }
.view-records-list { list-style: none; padding: 0; margin: 0; }
.view-records-list li { border-bottom: 1px solid var(--site-border, rgba(128, 128, 128, .2)); }
.view-record-link { display: grid; grid-template-columns: minmax(0, 1fr) 64px 112px 16px; align-items: center; gap: 24px; padding: 22px 0; text-decoration: none; color: var(--site-text-secondary); cursor: var(--site-interactive-cursor); font: 15px/1.45 'Quattrocento Sans', sans-serif; transition: color 180ms ease; }
.view-record-copy { display: grid; gap: 5px; min-width: 0; }
.view-record-copy strong { color: var(--site-accent-ink); font-weight: 600; font-size: 18px; }
.view-record-copy > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.view-record-link svg { width: 16px; height: 16px; }
.view-record-link time { text-align: right; }
.view-record-state { font-size: 13px; }
.view-records-more { margin-top: 20px; padding: 8px 0; background: none; border: 0; color: var(--site-text-secondary); font: 16px 'Quattrocento Sans', sans-serif; cursor: var(--site-interactive-cursor); }
.view-record-link:focus-visible, .view-records-more:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
@media (hover: hover) { .view-record-link:hover, .view-record-link:hover strong, .view-records-more:hover { color: var(--site-accent-ink); } }
@media (max-width: 600px) {
  .view-record-link { grid-template-columns: 1fr auto 16px; gap: 8px 16px; }
  .view-record-copy { grid-column: 1 / 3; }
  .view-record-link svg { grid-column: 3; grid-row: 1; }
  .view-record-link time { text-align: left; grid-column: 1; grid-row: 2; font-size: 13px; }
  .view-record-state { grid-column: 2; grid-row: 2; }
}
@media (prefers-reduced-motion: reduce) { .view-record-link { transition: none; } }

@import url('../assets/css/list.css');

.public-views {
  width: 100%;
  padding-bottom: 2rem;
  color: #343434;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.public-views-hero { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 6fr); align-items: start; gap: 48px; margin: 30px 0 64px; }
.public-views-hero h1 { margin: 0; text-wrap: balance; }
.public-views-hero h1 span { display: block; font-weight: inherit; font-style: normal; }
.public-views-dek { margin: 0; color: var(--site-text-secondary); font: inherit; }
.view-term-definition { color: var(--site-accent-ink); }
.public-views-invitation { display: inline-flex; align-items: center; justify-content: flex-start; gap: 11px; width: fit-content; max-width: 100%; min-height: 48px; margin-top: 20px; padding: 0; border: 0; background: transparent; color: inherit; font: 15px/1.4 'Quattrocento Sans', sans-serif; text-align: left; cursor: var(--site-interactive-cursor); -webkit-tap-highlight-color: transparent; }
.public-views-project { appearance:none;border:0;background:none;padding:0;display: inline-flex; align-items: center; gap: 8px; min-height: 44px; margin-top: 18px; color: var(--site-text-secondary); font: 14px/1.4 'Quattrocento Sans', sans-serif; text-decoration: none; cursor: var(--site-interactive-cursor); transition: color 180ms ease; -webkit-tap-highlight-color: transparent; }
.public-views-project svg { flex: 0 0 auto; width: 15px; height: 15px; transition: transform 180ms cubic-bezier(.23,1,.32,1); }
.public-views-project:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.public-views-project:active { color: var(--site-accent-ink); }
@media (hover: hover) and (pointer: fine) {
  .public-views-project:hover { color: var(--site-accent-ink); }
  .public-views-project:hover svg { transform: translate(2px,-2px); }
}
@media (prefers-reduced-motion: reduce) {
  .public-views-project, .public-views-project svg { transition: none; }
  .public-views-project:hover svg { transform: none; }
}
.public-views-invitation-copy { background: linear-gradient(rgba(var(--site-accent-rgb), .16), rgba(var(--site-accent-rgb), .16)) 0 100% / 0 42% no-repeat; transition: color 180ms ease, background-size 180ms cubic-bezier(.23,1,.32,1); }
.public-views-compose-icon { flex: 0 0 21px; width: 21px; height: 21px; color: var(--site-accent); overflow: visible; }
.public-views-compose-pen { transform-origin: 10px 14px; transition: transform 180ms cubic-bezier(.23,1,.32,1); }
.public-views-invitation:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.public-views-invitation:active { transform: scale(.99); }
@media (hover: hover) and (pointer: fine) {
  .public-views-invitation:hover .public-views-invitation-copy { color: var(--site-accent-ink); background-size: 100% 42%; }
  .public-views-invitation:hover .public-views-compose-pen { transform: rotate(-7deg); }
}
.public-views-collection-heading p { color: var(--site-accent-muted); }
.public-views-collection-heading { position: relative; margin-bottom: 0; }
.public-views-title-line { display: flex; align-items: baseline; gap: 0.28rem; }
.public-views-collection { display: grid; grid-template-columns: minmax(0, 1fr) 250px; gap: 0 48px; scroll-margin-top: 1rem; }
.public-views-collection > * { grid-column: 1 / -1; }
.public-views-collection-heading { grid-column: 1; grid-row: 1; }
.public-views-collection .public-views-tools { grid-column:2;grid-row:1;align-self:end;display:flex;flex-direction:column;align-items:flex-end;gap:22px;margin:0;padding:0;border:0; }
.public-views-tools .public-views-search {width:100%;flex:0 0 auto;min-height:42px;box-sizing:border-box;}
.public-views-collection .public-views-list { margin-top: 32px; padding-top: 32px; border-top: 1px solid color-mix(in srgb, currentColor 16%, transparent); }
.public-views-collection-heading h2 {
  margin: 0;
  font-size: 1.5rem;
  line-height: 1.3;
}
.public-views-collection-heading p { max-width: 27rem; margin: 0.35rem 0 0; font-size: 0.82rem; line-height: 1.5; }
.public-views-admin-trigger {
  display: grid;
  width: 1.65rem;
  height: 1.65rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--site-accent);
  font: 0.78rem/1 sans-serif;
  cursor: pointer;
  opacity: 0.16;
  place-items: center;
  transform-origin: center;
  transition: opacity 160ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.public-views-admin-trigger:hover,
.public-views-admin-trigger:focus-visible,
.public-views-admin-trigger[aria-expanded="true"] { opacity: 0.72; transform: rotate(18deg) scale(1.08); }
.public-views-admin-trigger:focus-visible { outline: 1px solid currentColor; outline-offset: 2px; }
.public-views-admin-trigger:active { transform: rotate(18deg) scale(0.92); transition-duration: 80ms; }
.public-views-admin-login {
  position: relative;
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) minmax(10rem, 14rem) auto;
  align-items: end;
  gap: 0.85rem;
  margin-top: 0.9rem;
  padding: 1rem 2.75rem 1rem 1.05rem;
  background: color-mix(in srgb, #f7f6f2 92%, var(--site-accent));
  box-shadow: 0 12px 36px rgba(34, 29, 22, 0.1);
  font-family: "Quattrocento Sans", sans-serif;
  transform-origin: 1.75rem top;
}
.public-views-admin-login strong { display: block; font-size: 0.88rem; }
.public-views-admin-login p { margin: 0.28rem 0 0; color: #6e6a63; font-size: 0.75rem; line-height: 1.4; }
.public-views-admin-login label { display: block; }
.public-views-admin-login input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.58rem 0.65rem;
  border: 1px solid rgba(65, 59, 50, 0.22);
  border-radius: 0;
  background: rgba(255, 255, 255, 0.62);
  color: inherit;
  font: inherit;
  outline: 0;
}
.public-views-admin-login input:focus { border-color: var(--site-accent); box-shadow: 0 0 0 3px rgba(var(--site-accent-rgb), 0.1); }
.public-views-admin-login button,
.public-views-admin-bar button,
.public-views-reading-admin button,
.public-view-admin-controls button {
  padding: 0.55rem 0.65rem;
  border: 0;
  border-radius: 0;
  background: rgba(var(--site-accent-rgb), 0.14);
  color: inherit;
  font: 600 0.72rem/1 "Quattrocento Sans", sans-serif;
  cursor: pointer;
  transition: background-color 140ms ease, opacity 140ms ease, transform 90ms ease-out;
}
.public-views-admin-login button:hover,
.public-views-admin-login button:focus-visible,
.public-views-admin-bar button:hover,
.public-views-admin-bar button:focus-visible,
.public-views-reading-admin button:hover,
.public-views-reading-admin button:focus-visible,
.public-view-admin-controls button:hover,
.public-view-admin-controls button:focus-visible { background: rgba(var(--site-accent-rgb), 0.24); }
.public-views-admin-login button:active,
.public-views-admin-bar button:active,
.public-views-reading-admin button:active,
.public-view-admin-controls button:active { transform: scale(0.96); }
.public-views-admin-login button:disabled,
.public-views-admin-bar button:disabled,
.public-views-reading-admin button:disabled,
.public-view-admin-controls button:disabled { cursor: default; opacity: 0.35; transform: none; }
.public-views-admin-close {
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0 !important;
  background: transparent !important;
  font-size: 1rem !important;
}
.public-views-admin-error { grid-column: 1 / -1; color: #925447 !important; }
.public-admin-panel-enter-active,
.public-admin-panel-leave-active { transition: opacity 160ms ease, transform 220ms cubic-bezier(0.22, 1, 0.36, 1); }
.public-admin-panel-enter-from,
.public-admin-panel-leave-to { opacity: 0; transform: translateY(-5px) scale(0.99); }
.public-views-admin-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.9rem;
  padding: 0.75rem 0.85rem;
  background: rgba(var(--site-accent-rgb), 0.11);
  font-family: "Quattrocento Sans", sans-serif;
}
.public-views-admin-bar > div { display: grid; gap: 0.12rem; margin-right: auto; }
.public-views-admin-bar strong { font-size: 0.8rem; }
.public-views-admin-bar span { color: var(--site-accent-muted); font-size: 0.7rem; }
.public-views-reading-admin { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 18px 0; border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent); font-family: 'Quattrocento Sans', sans-serif; }
.public-views-reading-admin strong { font-size: 14px; }
.public-views-reading-admin p { margin: 4px 0 0; color: var(--site-text-secondary); font-size: 13px; line-height: 1.5; }
.public-views-reading-admin .public-views-reading-message { color: var(--site-accent-ink); }
.public-views-reading-admin button { display: flex; align-items: center; justify-content: center; gap: 8px; flex-shrink: 0; min-height: 40px; padding: 10px 14px; }
.reading-update-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.public-views-search {
  margin: 0;
}
.public-views-tools { margin: 24px 0 32px; padding-bottom: 20px; border-bottom: 1px solid color-mix(in srgb, currentColor 16%, transparent); }
.public-views-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 48px 40px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.public-views-list > li { border-bottom: 1px solid color-mix(in srgb, currentColor 16%, transparent); }
.public-views-list.is-curating > li { display: flex; flex-direction: column; min-width: 0; }
.public-views-list.is-curating .public-view-link { height: auto; flex: 1 0 auto; }
.public-views-list.is-curating .public-view-admin-controls { flex: 0 0 auto; flex-wrap: wrap; gap: 6px; margin-top: auto; padding: 12px 10px; }
.public-views-list.is-curating .public-view-admin-controls button { min-height: 36px; }
.public-view-admin-controls button:focus-visible,
.public-views-reading-admin button:focus-visible { outline: 2px solid var(--site-accent-ink); outline-offset: 3px; }
.public-views-list li:first-child { grid-column: 1 / -1; }
.public-view-reveal-enter-active {
  transition:
    opacity 200ms ease-out var(--public-view-delay),
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1) var(--public-view-delay);
}
.public-view-reveal-enter-from {
  opacity: 0;
  transform: translate3d(0, 10px, 0) scale(0.997);
}
.public-view-reveal-move {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.public-view-link { position: relative; display: flex; flex-direction: column; height: 100%; color: inherit; text-decoration: none; cursor: var(--site-interactive-cursor); transition: color 160ms ease, transform 120ms ease-out; }
.public-view-link:focus-visible { outline: 2px solid currentColor; outline-offset: 6px; }
.public-view-link:active { transform: scale(.995); }
.public-view-plate { position: relative; aspect-ratio: 4 / 1; margin: 0; overflow: hidden; background: color-mix(in srgb, var(--site-accent) 5%, transparent); }
.public-view-plate img { display: block; width: 100%; height: 100%; margin: 0; border-radius: 0; object-fit: contain; transition: transform 240ms cubic-bezier(.23,1,.32,1); }
.public-view-body { position: relative; display: flex; flex-direction: column; flex: 1; gap: 12px; padding: 22px 0 24px; }
.public-view-question { color: var(--site-text-secondary); font: 13px/1.55 'Quattrocento Sans', sans-serif; text-wrap: pretty; }
.public-views-list strong { font: 400 25px/1.3 'PT Serif', Georgia, serif; letter-spacing: -.018em; text-wrap: pretty; }
.public-view-meta { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-top: auto; padding-top: 14px; color: var(--site-text-tertiary); font: 12px/1.4 'Quattrocento Sans', sans-serif; }
.public-view-meta .site-arrow-up-right { flex: 0 0 auto; width: 16px; height: 16px; transition: transform 180ms cubic-bezier(.23,1,.32,1); }
.public-views-list li:first-child .public-view-link.has-illustration { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 40px; padding-bottom: 32px; }
.public-views-list li:first-child .public-view-plate { align-self: center; aspect-ratio: 4 / 1; }
.public-views-list li:first-child .public-view-body { justify-content: center; padding: 10px 0; border: 0; }
.public-views-list li:first-child .public-view-meta { margin-top: 12px; }
.public-views-list li:first-child strong { font-size: 30px; }
@media (hover: hover) and (pointer: fine) {
  .public-view-link:hover .public-view-plate img { transform: scale(1.025); }
  .public-view-link:hover strong { color: var(--site-accent-ink); }
  .public-view-link:hover .public-view-meta .site-arrow-up-right { transform: translate(2px,-2px); }
}
.public-view-admin-controls {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.6rem;
  border-top: 1px solid rgba(70, 63, 53, 0.1);
  background: rgba(var(--site-accent-rgb), 0.075);
}
.public-view-visibility {
  min-width: 3.2rem;
  margin-right: auto;
  color: #4e7164;
  font: 700 0.64rem/1 "Quattrocento Sans", sans-serif;
  letter-spacing: 0.025em;
}
.public-view-visibility.is-hidden { color: #8b7765; }
.public-view-admin-controls .is-destructive { color: #954f45; }
.is-admin-hidden .public-view-link { background: rgba(104, 91, 74, 0.055); }
.is-admin-hidden .public-view-plate img { filter: grayscale(0.7); opacity: 0.48; }
.is-admin-hidden .public-view-body { opacity: 0.62; }
.is-admin-pending { pointer-events: none; opacity: 0.55; }
.public-views-status { color: #777; font-family: "Quattrocento Sans", sans-serif; font-size: 0.8rem; }
.public-views-status p { margin: 0 0 0.5rem; }
.public-views-retry {
  min-height: 32px;
  padding: 0.2rem 0;
  border: 0;
  border-bottom: 1px solid currentColor;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: opacity 160ms ease, transform 160ms ease;
}
.public-views-retry:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
.public-views-retry:active { transform: scale(0.97); }
@media (hover: hover) and (pointer: fine) {
  .public-views-retry:hover { opacity: 0.75; }
}
:global(html.dark-mode .public-views),
:global(body.dark-mode .public-views) { color: #e2e2e2; }
:global(html.dark-mode .view-term-definition),
:global(body.dark-mode .view-term-definition) { color: #e0e7e4; }
:global(html.dark-mode .public-views-dek),
:global(html.dark-mode .public-view-question),
:global(html.dark-mode .public-views-status),
:global(html.dark-mode .public-views-collection-heading p),
:global(body.dark-mode .public-views-dek),
:global(body.dark-mode .public-view-question),
:global(body.dark-mode .public-views-status),
:global(body.dark-mode .public-views-collection-heading p) { color: var(--site-text-secondary); }
:global(html.dark-mode .public-views-admin-login),
:global(body.dark-mode .public-views-admin-login) { background: color-mix(in srgb, #242425 90%, var(--site-accent)); }
:global(html.dark-mode .public-views-admin-login input),
:global(body.dark-mode .public-views-admin-login input) { background: rgba(0, 0, 0, 0.22); border-color: rgba(255, 255, 255, 0.16); }
:global(html.dark-mode .public-view-admin-controls),
:global(body.dark-mode .public-view-admin-controls) { border-top-color: rgba(255, 255, 255, 0.08); }
:global(html.dark-mode .public-view-meta),
:global(body.dark-mode .public-view-meta) { color: var(--site-text-tertiary); }
@media (prefers-reduced-motion: reduce) {
  .public-view-reveal-enter-active {
    transition: opacity 120ms linear;
    transition-delay: 0ms;
  }
  .public-view-reveal-enter-from { transform: none; }
  .public-view-reveal-move { transition: none; }
  .public-view-link, .public-view-meta .site-arrow-up-right,
  .public-view-plate img,
  .public-views-invitation,
  .public-views-compose-pen,
  .public-views-invitation-copy,
  .public-views-admin-trigger,
  .public-views-retry,
  .public-views-reading-admin button,
  .public-view-admin-controls button { transition-duration: 0.01ms; }
  .public-admin-panel-enter-active,
  .public-admin-panel-leave-active { transition: opacity 120ms linear; }
  .public-admin-panel-enter-from,
  .public-admin-panel-leave-to { transform: none; }
}
@media (max-width: 720px) {
  .public-views-collection { grid-template-columns: 1fr; scroll-margin-top: 64px; }
  .public-views-collection .public-views-tools { grid-column: 1; grid-row: auto; margin-top: 24px; }
  .public-views-collection .public-views-list { padding-top: 0; border-top: 0; }
  .public-views-tools { display: flex; }
  .public-views-list { grid-template-columns: 1fr; gap: 32px; }
  .public-views-hero { grid-template-columns: 1fr; gap: 24px; margin-bottom: 40px; }
  .public-views-list li:first-child .public-view-link.has-illustration { display: flex; gap: 0; padding-bottom: 0; }
  .public-views-list li:first-child .public-view-plate { align-self: stretch; aspect-ratio: 4 / 1; }
  .public-views-list li:first-child .public-view-body { padding: 22px 0 24px; }
  .public-views-list li:first-child { grid-column: auto; }
}
@media (min-width: 721px) and (max-width: 1100px) {
  .public-views-collection { scroll-margin-top: 78px; }
}
@media (max-width: 580px) {
  .public-views-list li:first-child strong { font-size: 25px; }
  .public-views-search { flex: 1 1 100%; width: 100%; }
  .public-views-admin-login { grid-template-columns: 1fr; align-items: stretch; }
  .public-views-admin-login button[type="submit"] { min-height: 2.35rem; }
  .public-views-admin-bar { align-items: stretch; flex-wrap: wrap; }
  .public-views-admin-bar > div { flex: 1 1 100%; }
  .public-views-reading-admin { align-items: stretch; flex-direction: column; gap: 12px; }
  .public-views-reading-admin button { align-self: flex-start; }
  .public-view-admin-controls { flex-wrap: wrap; }
  .public-view-visibility { flex: 1 1 100%; margin-bottom: 0.15rem; }
}
</style>

<style scoped>
.public-views-museum-return { margin:0 0 32px;padding:8px 0;border:0;border-bottom:1px solid currentColor;background:none;color:var(--site-text-secondary);font:14px 'Quattrocento Sans',sans-serif;cursor:pointer;display:flex;gap:25px;align-items:center; }
</style>
